import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, useLang, useUnits, formatCurrency, formatApiError, usePerm } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, Section, Select } from "../components/primitives";
import CatalogPicker from "../components/CatalogPicker";
import QuoteCsvImportModal from "../components/QuoteCsvImportModal";
import AufmassFromQuoteModal from "../components/AufmassFromQuoteModal";
import VatRatePicker from "../components/VatRatePicker";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft, FloppyDisk, FilePdf, Receipt, FolderPlus, ArrowsClockwise, Ruler, UploadSimple } from "@phosphor-icons/react";

function pickTierPrice(position, qty) {
  if (!position || !position.tiers?.length) return position?.unit_price ?? 0;
  const sorted = [...position.tiers].sort((a, b) => a.min_quantity - b.min_quantity);
  let price = position.unit_price;
  for (const tr of sorted) {
    if (Number(qty) >= Number(tr.min_quantity)) price = tr.unit_price;
  }
  return price;
}

const emptyLine = { position_id: null, name: "", description: "", unit: "Stk", quantity: 1, unit_price: 0, discount_percent: 0 };

export default function QuoteEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { units, byValue: unitByValue } = useUnits();

  const [positions, setPositions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    number: "",
    title: "",
    customer_id: "",
    status: "draft",
    vat_rate: 19,
    vat_note: "",
    notes: "",
    valid_until: "",
    purchase_order_number: "",
    lines: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [aufmassModalOpen, setAufmassModalOpen] = useState(false);
  const { can: canPerm } = usePerm();
  const canCreateAufmass = canPerm("aufmasse", "create");
  const canViewAufmass = canPerm("aufmasse", "view");
  const canImportCsv = canPerm("positions", "create");
  const [linkedAufmasse, setLinkedAufmasse] = useState([]);

  const addImportedLines = (lines) => {
    setForm((f) => ({ ...f, lines: [...f.lines, ...lines.map((l) => ({ ...l }))] }));
  };

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/positions"), api.get("/customers")]);
      setPositions(p.data);
      setCustomers(c.data);
      if (!isNew) {
        const q = await api.get(`/quotes/${id}`);
        setForm({
          number: q.data.number || "",
          title: q.data.title || "",
          customer_id: q.data.customer_id || "",
          status: q.data.status,
          vat_rate: q.data.vat_rate,
          vat_note: q.data.vat_note || "",
          notes: q.data.notes || "",
          valid_until: q.data.valid_until || "",
          purchase_order_number: q.data.purchase_order_number || "",
          lines: q.data.lines || [],
        });
        if (canViewAufmass) {
          try {
            const am = await api.get("/aufmasse", { params: { source_quote_id: id } });
            setLinkedAufmasse(am.data || []);
          } catch { /* keine Berechtigung / Fehler → Liste bleibt leer */ }
        }
      }
      setLoading(false);
    })();
  }, [id, isNew]);

  const totals = useMemo(() => {
    const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount_percent || 0) / 100), 0);
    const vat = subtotal * (Number(form.vat_rate || 0) / 100);
    return { subtotal, vat, total: subtotal + vat };
  }, [form.lines, form.vat_rate]);

  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const addEmpty = () => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] });

  const addFromCatalog = (pos) => {
    const qty = 1;
    const price = pickTierPrice(pos, qty);
    setForm({
      ...form,
      lines: [
        ...form.lines,
        {
          position_id: pos.id,
          name: pos.name,
          description: pos.description || "",
          unit: pos.unit || "Stk",
          quantity: qty,
          unit_price: price,
          discount_percent: 0,
        },
      ],
    });
    setPickerOpen(false);
  };

  const saveLineToCatalog = async (i) => {
    const l = form.lines[i];
    if (!l.name) return;
    try {
      const { data } = await api.post("/positions/from-free-line", {
        name: l.name, description: l.description || "", unit: l.unit || "Stk",
        quantity: 1, unit_price: Number(l.unit_price) || 0,
      });
      // Mark this line as linked to the new catalog position
      updateLine(i, { position_id: data.id });
      toast.success(`${data.number} → ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createSupplement = async () => {
    if (isNew) { toast.info(t("mustSaveFirst")); return; }
    try {
      const { data } = await api.post(`/quotes/${id}/supplement`);
      navigate(`/quotes/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const onQuantityChange = (i, qty) => {
    const l = form.lines[i];
    const patch = { quantity: qty };
    if (l.position_id) {
      const pos = positions.find((p) => p.id === l.position_id);
      if (pos) patch.unit_price = pickTierPrice(pos, qty);
    }
    updateLine(i, patch);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        number: form.number || null,
        title: form.title,
        customer_id: form.customer_id || null,
        status: form.status,
        vat_rate: Number(form.vat_rate) || 0,
        vat_note: form.vat_note || "",
        notes: form.notes,
        valid_until: form.valid_until || null,
        purchase_order_number: form.purchase_order_number || "",
        lines: form.lines.map((l) => ({
          position_id: l.position_id,
          name: l.name,
          description: l.description || "",
          unit: l.unit || "Stk",
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          discount_percent: Number(l.discount_percent) || 0,
        })),
      };
      if (isNew) {
        const { data } = await api.post("/quotes", payload);
        navigate(`/quotes/${data.id}`);
      } else {
        const { data } = await api.put(`/quotes/${id}`, payload);
        setForm((f) => ({ ...f, number: data.number }));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const convertToInvoice = async () => {
    if (isNew) { toast.info(t("mustSaveFirst")); return; }
    try {
      const { data } = await api.post(`/quotes/${id}/convert-to-invoice`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadPdf = async () => {
    if (isNew) { toast.info(t("mustSaveFirst")); return; }
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { responseType: "blob", params: { lang } });
      const filename = `${form.number || id}.pdf`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">…</div>;

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <Link to="/quotes" className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)] hover:text-[var(--ink)]" data-testid="back-to-quotes">
          <ArrowLeft size={14} /> {t("quotes")}
        </Link>
      </div>

      <PageHeader
        eyebrow={isNew ? "New" : "Edit"}
        title={isNew ? t("newQuote") : t("editQuote")}
        actions={
          <div className="flex gap-2 flex-wrap">
            {!isNew && (
              <>
                <Btn variant="secondary" onClick={downloadPdf} data-testid="quote-download-pdf">
                  <FilePdf size={16} /> {t("downloadPdf")}
                </Btn>
                <Btn variant="secondary" onClick={createSupplement} data-testid="quote-supplement">
                  <ArrowsClockwise size={16} /> {lang === "de" ? "Nachtrag" : "Supplement"}
                </Btn>
                {canCreateAufmass && (
                  <Btn variant="secondary" onClick={() => setAufmassModalOpen(true)} data-testid="quote-create-aufmass">
                    <Ruler size={16} /> {lang === "de" ? "Aufmaß" : "Aufmaß"}
                  </Btn>
                )}
                <Btn variant="accent" onClick={convertToInvoice} data-testid="quote-to-invoice">
                  <Receipt size={16} /> {t("convertToInvoice")}
                </Btn>
              </>
            )}
            <Btn onClick={save} disabled={saving} data-testid="quote-save">
              <FloppyDisk size={16} /> {saving ? "…" : t("save")}
            </Btn>
          </div>
        }
      />

      {error && <div className="mb-4 text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: meta + lines */}
        <div className="lg:col-span-2 space-y-6">
          <Section title={t("details")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("number")}>
                <TextInput data-testid="quote-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="A-2026-0001" />
              </Field>
              <Field label={t("title")}>
                <TextInput data-testid="quote-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Field>
              <Field label={t("customer")}>
                <select data-testid="quote-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  className="w-full h-10 px-3 bg-white border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none">
                  <option value="">— {t("noCustomer")} —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company || c.name || "—"}{c.company && c.name ? ` · ${c.name}` : ""}</option>)}
                </select>
              </Field>
              <Field label={t("status")}>
                <select data-testid="quote-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full h-10 px-3 bg-white border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 focus:outline-none">
                  <option value="draft">{t("draft")}</option>
                  <option value="sent">{t("sent")}</option>
                  <option value="accepted">{t("accepted")}</option>
                  <option value="rejected">{t("rejected")}</option>
                </select>
              </Field>
              <Field label={t("vatRate")}>
                <VatRatePicker
                  value={form.vat_rate}
                  note={form.vat_note}
                  onChange={(rate, note) => setForm({ ...form, vat_rate: rate, vat_note: note })}
                />
              </Field>
              <Field label={t("validUntil")}>
                <TextInput type="date" data-testid="quote-valid-until" value={form.valid_until || ""} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </Field>
              <Field label={t("purchaseOrderNumber")}>
                <TextInput
                  data-testid="quote-po-number"
                  value={form.purchase_order_number}
                  onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
                  placeholder={lang === "de" ? "z. B. BST-2026-001" : "e.g. PO-2026-001"}
                />
              </Field>
              <Field label={t("notes")} className="md:col-span-2">
                <TextArea data-testid="quote-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
          </Section>

          <Section
            title={t("positions")}
            right={
              <div className="flex gap-2">
                <Btn variant="secondary" onClick={() => setPickerOpen(true)} data-testid="pick-position-btn"><Plus size={14} /> {t("pickPosition")}</Btn>
                <Btn variant="secondary" onClick={addEmpty} data-testid="add-empty-line-btn"><Plus size={14} /> {t("emptyLine")}</Btn>
                {canImportCsv && (
                  <Btn variant="secondary" onClick={() => setCsvImportOpen(true)} data-testid="quote-csv-import-btn"><UploadSimple size={14} /> {lang === "de" ? "CSV-Import" : "CSV import"}</Btn>
                )}
              </div>
            }
          >
            {form.lines.length === 0 ? (
              <div className="text-sm text-[var(--muted-2)] py-6 text-center">
                {lang === "de" ? "Noch keine Positionen im Angebot." : "No lines yet."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[var(--muted)]">
                    <tr className="border-b border-[var(--border)]">
                      <th className="text-left py-2 label-overline pr-2">{t("description")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-20">{t("quantity")}</th>
                      <th className="text-left py-2 label-overline pr-2 w-20">{t("unit")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-28">{t("unitPrice")}</th>
                      <th className="text-right py-2 label-overline pr-2 w-20">{t("discount")} %</th>
                      <th className="text-right py-2 label-overline pr-2 w-28">{t("lineTotal")}</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, i) => {
                      const lineTotal = Number(l.quantity || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount_percent || 0) / 100);
                      const unitInfo = unitByValue[l.unit] || {};
                      const integerOnly = unitInfo.integer_only;
                      return (
                        <tr key={i} className="border-b border-[var(--border)] align-top">
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`line-name-${i}`} value={l.name} onChange={(e) => updateLine(i, { name: e.target.value })} className="mb-1" placeholder={t("positionName")} />
                            <TextInput data-testid={`line-desc-${i}`} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder={t("description")} />
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput
                              data-testid={`line-qty-${i}`}
                              type="number"
                              step={integerOnly ? "1" : "0.01"}
                              min="0"
                              className="text-right"
                              value={l.quantity}
                              onChange={(e) => {
                                const v = e.target.value;
                                onQuantityChange(i, integerOnly && v !== "" ? String(parseInt(v, 10) || 0) : v);
                              }}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Select data-testid={`line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}>
                              {units.map((u) => (
                                <option key={u.value} value={u.value}>{u.value}</option>
                              ))}
                            </Select>
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`line-price-${i}`} type="number" step="0.01" className="text-right" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                          </td>
                          <td className="py-2 pr-2">
                            <TextInput data-testid={`line-discount-${i}`} type="number" step="0.1" className="text-right" value={l.discount_percent} onChange={(e) => updateLine(i, { discount_percent: e.target.value })} />
                          </td>
                          <td className="py-2 pr-2 text-right font-medium whitespace-nowrap">
                            {formatCurrency(lineTotal, lang)}
                          </td>
                          <td className="py-2">
                            <div className="flex gap-1 mt-1">
                              {!l.position_id && l.name && (
                                <button type="button" onClick={() => saveLineToCatalog(i)} data-testid={`line-to-catalog-${i}`}
                                  className="text-[var(--muted)] hover:text-[var(--brand)]" title={lang === "de" ? "Zum Katalog hinzufügen" : "Add to catalog"}>
                                  <FolderPlus size={14} />
                                </button>
                              )}
                              <button type="button" onClick={() => removeLine(i)} data-testid={`line-remove-${i}`} className="text-[var(--muted-2)] hover:text-[#FF3B30]">
                                <Trash size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {!isNew && linkedAufmasse.length > 0 && (
            <Section title={lang === "de" ? `Aufmaße zu diesem Angebot (${linkedAufmasse.length})` : `Aufmaße for this quote (${linkedAufmasse.length})`}>
              <div className="divide-y divide-[var(--border)]" data-testid="quote-linked-aufmasse">
                {linkedAufmasse.map((am) => (
                  <Link
                    key={am.id}
                    to={`/aufmasse/${am.id}`}
                    className="flex items-center justify-between gap-3 py-2.5 px-1 hover:bg-[var(--row-hover)] rounded-sm"
                    data-testid={`quote-aufmass-${am.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Ruler size={15} className="text-[var(--muted)] shrink-0" />
                      <span className="font-medium font-mono text-sm whitespace-nowrap">{am.number}</span>
                      {am.title && <span className="text-sm text-[var(--muted)] truncate">{am.title}</span>}
                      {am.is_storno && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#FF3B30]/10 text-[#FF3B30]">Storno</span>
                      )}
                      {am.has_signed_pdf && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{lang === "de" ? "Signiert" : "Signed"}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-[var(--muted-2)]">
                      <span>{(am.measurement_date || am.created_at || "").slice(0, 10)}</span>
                      <span className="px-1.5 py-0.5 rounded-sm border border-[var(--border)]">
                        {lang === "de"
                          ? ({ draft: "Entwurf", signed: "Signiert", cancelled: "Storniert" }[am.status] || am.status)
                          : am.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Right: totals panel */}
        <div className="space-y-4">
          <div className="swiss-card p-6 sticky top-6">
            <div className="label-overline mb-4">{t("total")}</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--muted)]">{t("subtotal")}</span>
                <span className="font-mono">{formatCurrency(totals.subtotal, lang)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--muted)]">{t("vat")} ({form.vat_rate}%)</span>
                <span className="font-mono">{formatCurrency(totals.vat, lang)}</span>
              </div>
              <div className="h-px bg-[var(--border)] my-2" />
              <div className="flex justify-between items-baseline">
                <span className="font-heading text-base font-medium">{t("total")}</span>
                <span className="font-heading text-2xl font-semibold tracking-tight" data-testid="quote-total">
                  {formatCurrency(totals.total, lang)}
                </span>
              </div>
            </div>
            <div className="mt-6">
              <Btn onClick={save} disabled={saving} className="w-full h-11" data-testid="quote-save-side">
                <FloppyDisk size={16} /> {saving ? "…" : t("save")}
              </Btn>
            </div>
          </div>
        </div>
      </div>

      {/* Catalog picker */}
      <CatalogPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addFromCatalog} />
      <QuoteCsvImportModal open={csvImportOpen} onClose={() => setCsvImportOpen(false)} onLinesImported={addImportedLines} lang={lang} />
      <AufmassFromQuoteModal
        open={aufmassModalOpen}
        onClose={() => setAufmassModalOpen(false)}
        quote={isNew ? null : { ...form, id }}
        lang={lang}
      />
    </div>
  );
}
